import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getVanillaEl } from "../../domBridge";
import { apiFetch } from "../../../api";
import { requestLock } from "../../../lockBus";
import { showToast } from "../../../toast";
import { appState } from "../../../state";
import { getAutolockBridge } from "../../../autolockBridge";
import { useAudioPlayer, type Track } from "../../hooks/useAudioPlayer";

const MAX_TRACK_SIZE = 100 * 1024 * 1024; // matches server-side limit

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Click/tap-to-seek progress bar over the real <audio> element's own
// currentTime/duration (useAudioPlayer.ts) - no fabricated position.
function ProgressBar({ currentTime, duration, onSeek, disabled }: { currentTime: number; duration: number; onSeek: (t: number) => void; disabled: boolean }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const pct = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

  function handleSeek(clientX: number): void {
    const el = trackRef.current;
    if (!el || disabled || duration <= 0) return;
    const rect = el.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    onSeek(ratio * duration);
  }

  return (
    <div
      ref={trackRef}
      className={`music-progress${disabled ? " is-disabled" : ""}`}
      role="slider"
      aria-label="Seek"
      aria-valuemin={0}
      aria-valuemax={Math.round(duration)}
      aria-valuenow={Math.round(currentTime)}
      onPointerDown={(e) => handleSeek(e.clientX)}
    >
      <div className="music-progress-fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

function MusicPanelBody() {
  const [tracks, setTracks] = useState<Track[]>([]);
  const { audioRef, currentFilename, isPlaying, currentTime, duration, playTrack, seek } = useAudioPlayer();
  const uploadInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch("/api/music");
        if (res.status === 401) {
          requestLock("unauthorized");
          return;
        }
        if (!res.ok) throw new Error("Failed to load music");
        const data = await res.json();
        setTracks(Array.isArray(data.tracks) ? data.tracks : []);
      } catch (err) {
        console.error("Failed to load music:", err);
        setTracks([]);
      }
    })();
  }, []);

  async function uploadTracks(files: File[]): Promise<void> {
    if (!files.length) return;

    const tooBig = files.find((f) => f.size > MAX_TRACK_SIZE);
    if (tooBig) {
      showToast("One file is too large (max 100MB per track).", "error");
      return;
    }

    const dismissUploading = showToast(`Uploading ${files.length} track(s)…`, "info", 60_000);

    const fd = new FormData();
    files.forEach((f) => fd.append("music", f));

    appState.uploadInProgress = true;
    getAutolockBridge()?.resetInactivityTimer();

    try {
      const res = await apiFetch("/api/music/upload", { method: "POST", body: fd });
      dismissUploading();

      if (res.status === 401) {
        requestLock("unauthorized");
        return;
      }

      if (!res.ok) {
        let detail = "";
        try {
          const parsed = await res.json();
          detail = parsed.message || parsed.error || "";
        } catch {
          detail = "";
        }
        showToast(`Upload failed${detail ? " — " + detail : ""}`, "error");
        return;
      }

      showToast("Music uploaded ✅", "success");
      const refreshed = await apiFetch("/api/music");
      if (refreshed.ok) {
        const data = await refreshed.json();
        setTracks(Array.isArray(data.tracks) ? data.tracks : []);
      }
    } catch {
      dismissUploading();
      showToast("Upload failed. Try again.", "error");
    } finally {
      appState.uploadInProgress = false;
    }
  }

  function handleUploadBtnClick() {
    appState.filePickerOpen = true;
    getAutolockBridge()?.resetInactivityTimer();

    if (appState.pickerTimeoutId) clearTimeout(appState.pickerTimeoutId);
    appState.pickerTimeoutId = setTimeout(() => {
      appState.filePickerOpen = false;
      appState.pickerTimeoutId = null;
    }, 90_000);

    uploadInputRef.current?.click();
  }

  async function handleUploadInputChange() {
    appState.filePickerOpen = false;
    if (appState.pickerTimeoutId) {
      clearTimeout(appState.pickerTimeoutId);
      appState.pickerTimeoutId = null;
    }

    getAutolockBridge()?.resetInactivityTimer();
    const input = uploadInputRef.current;
    const files = Array.from(input?.files || []);
    if (input) input.value = "";
    await uploadTracks(files);
  }

  const currentTrack = tracks.find((t) => t.filename === currentFilename) ?? null;
  const currentIndex = currentFilename ? tracks.findIndex((t) => t.filename === currentFilename) : -1;
  const canSkip = tracks.length > 1;

  function playAdjacent(direction: 1 | -1): void {
    if (!canSkip) return;
    const base = currentIndex === -1 ? 0 : currentIndex;
    const nextIndex = (base + direction + tracks.length) % tracks.length;
    playTrack(tracks[nextIndex]);
  }

  return (
    <section>
      <div className="home-section-heading">
        <h2 className="home-section-title">
          <i className="bi bi-music-note-beamed me-2 text-pink" aria-hidden="true" />
          Music
        </h2>
        <span className="home-section-hint">Our soundtrack</span>
      </div>

      <div className="d-flex justify-content-end mb-3">
        <input
          ref={uploadInputRef}
          type="file"
          accept="audio/mpeg,audio/mp3,audio/mp4,audio/x-m4a,audio/wav,audio/x-wav,audio/ogg,audio/flac"
          multiple
          hidden
          aria-label="Select music to upload"
          onChange={handleUploadInputChange}
        />
        <button type="button" className="btn btn-outline-pink btn-sm" aria-label="Add music" onClick={handleUploadBtnClick}>
          <i className="bi bi-plus-lg me-1" aria-hidden="true" />
          Add
        </button>
      </div>

      {/* id="audioPlayer" is load-bearing: session.ts's lock handler does a
          fresh document.getElementById("audioPlayer")?.pause() (not a
          dom.ts import - see the comment there) to stop playback on lock -
          hiding the element's native chrome doesn't remove it from the DOM
          or affect playback, so that still works unchanged. */}
      <audio id="audioPlayer" ref={audioRef} style={{ display: "none" }} preload="none" aria-hidden="true">
        Your browser does not support the audio element.
      </audio>

      <div className="music-now-playing">
        <div className="music-now-playing-icon">
          <i className="bi bi-music-note-beamed" aria-hidden="true" />
        </div>
        <div className="music-now-playing-title text-truncate">{currentTrack ? currentTrack.title : "Nothing playing"}</div>
        <div className="music-now-playing-time">
          {formatTime(currentTrack ? currentTime : 0)} / {formatTime(currentTrack ? duration : 0)}
        </div>
        <ProgressBar currentTime={currentTime} duration={duration} onSeek={seek} disabled={!currentTrack} />
        <div className="music-controls">
          <button type="button" className="music-control-btn" aria-label="Previous track" onClick={() => playAdjacent(-1)} disabled={!canSkip}>
            <i className="bi bi-skip-start-fill" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="music-control-btn music-control-btn-main"
            aria-label={currentTrack && isPlaying ? "Pause" : "Play"}
            onClick={() => currentTrack && playTrack(currentTrack)}
            disabled={!currentTrack}
          >
            <i className={`bi ${currentTrack && isPlaying ? "bi-pause-fill" : "bi-play-fill"}`} aria-hidden="true" />
          </button>
          <button type="button" className="music-control-btn" aria-label="Next track" onClick={() => playAdjacent(1)} disabled={!canSkip}>
            <i className="bi bi-skip-end-fill" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="mt-3">
        <h3 className="small muted mb-2">Playlist</h3>
        <ul className="list-group" role="list" aria-label="Music playlist">
          {!tracks.length && (
            <li className="list-group-item music-empty text-center py-4 border-0 bg-transparent">
              <i className="bi bi-music-note-list d-block mb-2 text-pink" style={{ fontSize: "2rem", opacity: 0.5 }} />
              <div className="small fw-semibold mb-1">Your playlist is empty</div>
              <div className="small muted">Add music to get started.</div>
            </li>
          )}
          {tracks.map((t, idx) => {
            const isCurrent = t.filename === currentFilename;
            const trackIsPlaying = isCurrent && isPlaying;
            return (
              <li
                key={t.filename}
                className={`list-group-item d-flex align-items-center justify-content-between ${trackIsPlaying ? "is-playing" : ""}`}
              >
                <span className="d-flex align-items-center gap-2 min-w-0">
                  <span className="pill">{idx + 1}</span>
                  <span className="text-truncate">{t.title}</span>
                </span>
                <button
                  className="btn btn-sm btn-outline-pink track-play-btn flex-shrink-0"
                  type="button"
                  aria-label={`Play ${t.title}`}
                  onClick={() => playTrack(t)}
                >
                  <i className={`bi ${trackIsPlaying ? "bi-pause-fill" : "bi-play-fill"}`} aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

// Portals into #musicView, the dedicated "/music" page's container added
// in Phase 10 (previously a sidebar column inside #homeView, toggled by
// albums.ts alongside Home/album-detail switching - now its own top-level
// route, visibility owned by MusicRouteScreen.tsx via react-router).
export function MusicPanel() {
  const container = getVanillaEl<HTMLDivElement>("musicView");
  if (!container) return null;
  return createPortal(<MusicPanelBody />, container);
}
